import { useCallback, useEffect, useState } from 'react';
import { accountApi } from '../api/accountApi';
import { watchlistApi } from '../api/watchlistApi';

/**
 * Collect unique symbols from open positions + all Alpaca watchlists.
 */
export function useBookSymbols({ refreshMs = 60000 } = {}) {
  const [symbols, setSymbols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    try {
      setError('');
      const [positionsRes, watchlistsRes] = await Promise.all([
        accountApi.getPositions().catch(() => ({ data: [] })),
        watchlistApi.getAllWatchlists().catch(() => ({ data: [] })),
      ]);

      const set = new Set();
      (positionsRes.data || positionsRes || []).forEach((pos) => {
        if (pos?.symbol) set.add(String(pos.symbol).toUpperCase());
      });

      const lists = watchlistsRes.data || watchlistsRes || [];
      const detailed = await Promise.all(
        (Array.isArray(lists) ? lists : []).slice(0, 20).map(async (list) => {
          if (Array.isArray(list.assets) || Array.isArray(list.symbols)) return list;
          if (!list?.id) return list;
          try {
            const full = await watchlistApi.getWatchlistById(list.id);
            return full.data || full;
          } catch {
            return list;
          }
        })
      );

      detailed.forEach((list) => {
        const assets = list?.assets || list?.symbols || [];
        assets.forEach((asset) => {
          const symbol = typeof asset === 'string' ? asset : asset?.symbol;
          if (symbol) set.add(String(symbol).toUpperCase());
        });
      });

      setSymbols([...set].sort());
    } catch (err) {
      console.error('Failed to load book symbols:', err);
      setError(err.message || 'Failed to load book');
      setSymbols([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    if (!refreshMs) return undefined;
    const timer = setInterval(refresh, refreshMs);
    return () => clearInterval(timer);
  }, [refresh, refreshMs]);

  return { symbols, loading, error, refresh };
}
